import { IQuoteOfferingRespSchema } from '@monetize/types/app';
import { MGrid, MText } from '../../../../../components/Monetize';
import { formatQuoteSubtotal } from '../../../../../utils/quotes';

function getOfferingAmounts(
  quoteOffering?: IQuoteOfferingRespSchema | null,
  childQuoteOfferings?: IQuoteOfferingRespSchema[],
  currency?: string,
) {
  const output = {
    amount: quoteOffering?.amount || 0,
    amountWithoutDiscount: quoteOffering?.amountWithoutDiscount || 0,
  };
  if (Array.isArray(childQuoteOfferings)) {
    childQuoteOfferings.forEach(({ amount, amountWithoutDiscount }) => {
      output.amount += amount;
      output.amountWithoutDiscount += amountWithoutDiscount;
    });
  }

  return {
    amount: formatQuoteSubtotal(output.amount, currency),
    amountWithoutDiscount:
      output.amountWithoutDiscount - output.amount > 0
        ? formatQuoteSubtotal(output.amountWithoutDiscount, currency)
        : '',
  };
}

export interface QuoteOfferingActionsProps {
  quoteOffering?: IQuoteOfferingRespSchema | null;
  childQuoteOfferings?: IQuoteOfferingRespSchema[];
  currency?: string;
  /**
   * A rate segment's amount can show beneath the offering total, so it gets
   * its own test ids
   */
  isRateSegment?: boolean;
}

/**
 * Manages the quote offering menu based on the state of the quote offering
 */
export const QuoteOfferingAmount = ({
  quoteOffering,
  childQuoteOfferings = [],
  currency,
  isRateSegment,
}: QuoteOfferingActionsProps) => {
  if (!quoteOffering) {
    return null;
  }

  const testIdPrefix = isRateSegment
    ? `${quoteOffering.id}-rate`
    : quoteOffering.id;

  // get currency formatted values to show if offering is collapsed
  // combines parent and all scheduled offerings in amounts
  const { amount, amountWithoutDiscount } = getOfferingAmounts(
    quoteOffering,
    childQuoteOfferings,
    currency,
  );

  return (
    <MGrid gridTemplateColumns="1fr" minH={8} maxH={8} alignSelf="center">
      <>
        {!!amountWithoutDiscount.length && (
          <MText
            gridColumn="1"
            gridRow="1"
            textDecoration="line-through"
            fontWeight="bold"
            color="tGray.darkPurple"
            textAlign="right"
            fontSize="xs"
            alignSelf="start"
            data-testid={`${testIdPrefix}-offering-amountBeforeDiscount`}
          >
            {amountWithoutDiscount}
          </MText>
        )}
        <MText
          gridColumn="1"
          gridRow="1"
          textAlign="right"
          alignSelf={!amountWithoutDiscount.length ? 'center' : 'end'}
          fontWeight="bold"
          data-testid={`${testIdPrefix}-amount`}
        >
          {amount}
        </MText>
      </>
    </MGrid>
  );
};
